import { useToolIsMounted } from "#app/sites/site-tool-context";
import { useAISettings } from "@crm/ai/user-ai-credentials/use-ai-settings";
import { AiSettingsRpc } from "@crm/rpc-contract/groups/ai-settings";
import { reactivityInvalidateAtom } from "@crm/shared/query-atom";
import { useAtomSet } from "@effect/atom-react";
import { toErrorMessage } from "@crm/effect/client/mutation-workflow";
import { clientPromiseEffect } from "@crm/effect/client/promise-effect";
import { browserEffectRuntime } from "@crm/effect/client/runtime";
import type { AppError } from "@crm/errors";
import { Button } from "@crm/ui/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@crm/ui/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@crm/ui/components/ui/dialog";
import { Input } from "@crm/ui/components/ui/input";
import { Label } from "@crm/ui/components/ui/label";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Data, Effect, Option } from "effect";
import * as AsyncResult from "effect/unstable/reactivity/AsyncResult";
import { Check, Eye, EyeOff, Loader2, Pencil, Plus, Save, Trash2, X } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import type { ModelInfo } from "#app/sio/generator-types";
import { AVAILABLE_MODELS, getDefaultModel, isModelInList } from "#app/sio/models";
import {
  useAcfContexts,
  useCreateAcfContext,
  useDeleteAcfContext,
  useUpdateAcfContext,
} from "#app/site";
import { rpcCaller } from "@crm/rpc-client";
import { SiteAiTestRpc } from "@crm/rpc-contract/groups/site";
import { ModelSelect } from "#routes/_protected/sio-tools/-components/model-select";

export const Route = createFileRoute("/_protected/site-tools/_layout/settings")({
  head: () => ({}),
  component: SiteToolsSettingsPage,
});

function promiseEffect<A>(run: () => PromiseLike<A>) {
  return clientPromiseEffect("建站设置操作失败", run);
}

class SavedCredentialVersionError extends Data.TaggedError("SavedCredentialVersionError")<{
  readonly message: string;
}> {}

function runSiteToolsSettingsEffect<A>(effect: Effect.Effect<A, AppError>): Promise<A> {
  return browserEffectRuntime.runPromise(effect);
}

const aiSettingsRpc = rpcCaller(AiSettingsRpc);
const siteAiTest = rpcCaller(SiteAiTestRpc);

function fetchSavedModelListEffect() {
  return promiseEffect(() => siteAiTest.fetchSavedSiteToolsModelList(undefined));
}

function SiteToolsSettingsPage() {
  const navigate = useNavigate();
  const isMounted = useToolIsMounted();
  const invalidateAiSettings = useAtomSet(reactivityInvalidateAtom);
  const aiSettingsResult = useAISettings();
  const aiSettings = AsyncResult.value(aiSettingsResult).pipe(Option.getOrUndefined);
  const contextsResult = useAcfContexts();
  const contexts = AsyncResult.value(contextsResult).pipe(Option.getOrElse(() => []));
  const createContext = useCreateAcfContext();
  const updateContext = useUpdateAcfContext();
  const deleteContext = useDeleteAcfContext();

  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState("");
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // AI 配置状态
  const [showApiKey, setShowApiKey] = useState(false);
  const [apiKey, setApiKey] = useState("");
  const [baseUrl, setBaseUrl] = useState("");
  const [model, setModel] = useState("");
  const [isSavingConnection, setIsSavingConnection] = useState(false);
  const [isSavingModel, setIsSavingModel] = useState(false);
  const [isClearingCredential, setIsClearingCredential] = useState(false);
  const [apiModels, setApiModels] = useState<ModelInfo[]>([]);
  const [isLoadingModels, setIsLoadingModels] = useState(false);

  useEffect(() => {
    setBaseUrl(aiSettings?.aiBaseUrl ?? "");
    setModel(aiSettings?.aiModel ?? getDefaultModel("blog"));
  }, [aiSettings?.aiBaseUrl, aiSettings?.aiModel]);

  const savedBaseUrl = aiSettings?.aiBaseUrl?.trim() ?? "";
  const savedCredentialVersion = aiSettings?.configVersion;
  const inputApiKey = apiKey.trim();
  const inputBaseUrl = baseUrl.trim();
  const isConnectionSaved =
    (aiSettings?.configured ?? false) && !inputApiKey && inputBaseUrl === savedBaseUrl;

  const loadModelList = useCallback(() => {
    if (!(typeof savedCredentialVersion === "number" && savedCredentialVersion >= 1)) {
      return Promise.reject(
        new SavedCredentialVersionError({ message: "Saved credential has no usable version" }),
      );
    }
    setIsLoadingModels(true);
    return runSiteToolsSettingsEffect(
      fetchSavedModelListEffect().pipe(
        Effect.tap((models) =>
          Effect.sync(() => {
            setApiModels([...models]);
          }),
        ),
        Effect.ensuring(
          Effect.sync(() => {
            setIsLoadingModels(false);
          }),
        ),
      ),
    );
  }, [savedCredentialVersion]);

  useEffect(() => {
    if (!aiSettings?.configured) {
      setApiModels([]);
      return;
    }
    loadModelList().catch(() => {
      setApiModels([]);
    });
  }, [aiSettings?.configured, loadModelList]);

  const handleSaveAiSettings = () => {
    if (!inputApiKey) {
      toast.error("请输入 API Key");
      return;
    }
    setIsSavingConnection(true);
    void runSiteToolsSettingsEffect(
      Effect.gen(function* () {
        const result = yield* promiseEffect(() =>
          siteAiTest.testSiteToolsAIConnection({
            apiKey: inputApiKey,
            baseURL: inputBaseUrl || undefined,
          }),
        );
        if (!result.success) {
          yield* Effect.sync(() => {
            toast.error(result.error || "API Key 验证失败", {
              description: result.message,
              duration: 8000,
            });
          });
          return;
        }
        yield* promiseEffect(() =>
          aiSettingsRpc.updateAISettings({
            expectedVersion: aiSettings?.configVersion ?? 0,
            credentialMutation: { kind: "replace", value: inputApiKey },
            aiBaseUrl: inputBaseUrl || null,
          }),
        );
        yield* Effect.sync(() => {
          // AI 配置已迁移到 effect-atom：bump 域 signal 替代
          // `queryClient.setQueryData/invalidateQueries({ queryKey: userKeys.aiConfig() })`
          invalidateAiSettings(["aiSettings"]);
          setApiKey("");
          setShowApiKey(false);
        });
        yield* Effect.sync(() => {
          setIsLoadingModels(true);
        });
        const fetchedModels = yield* fetchSavedModelListEffect().pipe(
          Effect.tap((loadedModels) =>
            Effect.sync(() => {
              setApiModels([...loadedModels]);
            }),
          ),
          Effect.ensuring(
            Effect.sync(() => {
              setIsLoadingModels(false);
            }),
          ),
        );
        const models: ModelInfo[] = [...fetchedModels];
        if (!isModelInList(model, models)) {
          const fallbackModel = models[0]?.id ?? "";
          yield* Effect.sync(() => {
            setModel(fallbackModel);
          });
        }
        yield* Effect.sync(() => {
          toast.success("连接已保存，模型列表已更新", {
            description: result.details ? `API 连接正常 (${result.details.duration})` : undefined,
          });
        });
      }),
    )
      .catch((error) => {
        toast.error("保存失败", {
          description: toErrorMessage(error, "未知错误"),
        });
      })
      .finally(() => {
        setIsSavingConnection(false);
      });
  };

  const handleClearAiCredential = () => {
    if (!aiSettings?.configured) {
      return;
    }

    setIsClearingCredential(true);
    void runSiteToolsSettingsEffect(
      Effect.gen(function* () {
        yield* promiseEffect(() =>
          aiSettingsRpc.updateAISettings({
            expectedVersion: aiSettings.configVersion,
            credentialMutation: { kind: "clear" },
          }),
        );
        yield* Effect.sync(() => {
          setApiKey("");
          setShowApiKey(false);
          setApiModels([]);
          invalidateAiSettings(["aiSettings"]);
        });
      }),
    )
      .then(() => {
        toast.success("已清除保存的 API Key");
      })
      .catch((error) => {
        toast.error("清除失败", {
          description: toErrorMessage(error, "未知错误"),
        });
      })
      .finally(() => {
        setIsClearingCredential(false);
      });
  };

  const handleSaveModelSettings = () => {
    if (!isConnectionSaved) {
      toast.error("请先保存并验证当前 API 连接");
      return;
    }
    if (!model) {
      toast.error("请选择默认模型");
      return;
    }
    if (!isModelInList(model, apiModels)) {
      toast.error("选择的模型不在服务商返回的可用列表中");
      return;
    }
    setIsSavingModel(true);
    void runSiteToolsSettingsEffect(
      Effect.gen(function* () {
        const testResult = yield* promiseEffect(() =>
          siteAiTest.testSavedSiteToolsModelAvailability({
            model,
          }),
        );
        if (!testResult.success) {
          yield* Effect.sync(() => {
            toast.error(`模型不可用：${model}`, {
              description: testResult.message,
              duration: 10_000,
            });
          });
          return;
        }
        yield* promiseEffect(() =>
          aiSettingsRpc.updateAISettings({
            expectedVersion: aiSettings?.configVersion ?? 0,
            credentialMutation: { kind: "preserve" },
            aiModel: model,
          }),
        );
        yield* Effect.sync(() => {
          invalidateAiSettings(["aiSettings"]);
        });
        yield* Effect.sync(() => {
          toast.success("默认模型已保存");
        });
      }),
    )
      .catch((error) => {
        toast.error("保存失败", {
          description: toErrorMessage(error, "未知错误"),
        });
      })
      .finally(() => {
        setIsSavingModel(false);
      });
  };

  const canSelectModel = isConnectionSaved && apiModels.length > 0 && !isLoadingModels;

  const handleCreate = () => {
    const trimmed = newName.trim();
    if (!trimmed) {
      return;
    }
    return runSiteToolsSettingsEffect(
      Effect.gen(function* () {
        const result = yield* promiseEffect(() => createContext.executePromise({ name: trimmed }));
        yield* Effect.sync(() => {
          setNewName("");
          setShowCreate(false);
          if (isMounted())
            void navigate({ to: "/site-tools/acf", search: { contextId: result.id } });
        });
      }),
    );
  };

  const handleRename = () => {
    if (!(editingId && editingName.trim())) {
      return;
    }
    return runSiteToolsSettingsEffect(
      Effect.gen(function* () {
        yield* promiseEffect(() =>
          updateContext.executePromise({
            id: editingId,
            name: editingName.trim(),
          }),
        );
        yield* Effect.sync(() => {
          setEditingId(null);
          setEditingName("");
        });
      }),
    );
  };

  const handleDelete = () => {
    if (!deletingId) {
      return;
    }
    return runSiteToolsSettingsEffect(
      Effect.gen(function* () {
        yield* promiseEffect(() => deleteContext.executePromise(deletingId));
        yield* Effect.sync(() => {
          setDeletingId(null);
        });
      }),
    );
  };

  const deletingContext = contexts?.find((c) => c.id === deletingId);

  return (
    <div className="container mx-auto max-w-2xl px-4 py-8">
      <h1 className="mb-2 font-bold text-2xl text-foreground">设置</h1>
      <p className="mb-8 text-muted-foreground">配置建站工具的 AI 模型和 API 连接，管理项目</p>

      <div className="space-y-6">
        {/* API 配置 */}
        <Card>
          <CardHeader>
            <CardTitle>AI 配置</CardTitle>
            <CardDescription>
              配置你的 AI API 服务。支持 AIHubMix、OpenRouter 等 OpenAI 兼容
              API。配置会同步到云端，多设备共享。
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="baseUrl">API Base URL</Label>
              <Input
                autoComplete="off"
                id="baseUrl"
                onChange={(e) => setBaseUrl(e.target.value)}
                placeholder="https://aihubmix.com/v1 (默认)"
                value={baseUrl}
              />
              <p className="text-muted-foreground text-xs">
                留空则使用默认值。常见端点：
                <br />• AIHubMix:{" "}
                <code className="rounded bg-muted px-1 font-mono">https://aihubmix.com/v1</code>
                <br />• OpenRouter:{" "}
                <code className="rounded bg-muted px-1 font-mono">
                  https://openrouter.ai/api/v1
                </code>
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="apiKey">API Key</Label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Input
                    autoComplete="off"
                    className={`font-mono ${showApiKey ? "" : "text-transparent caret-transparent"}`}
                    id="apiKey"
                    onChange={(e) => setApiKey(e.target.value)}
                    onFocus={() => setShowApiKey(true)}
                    placeholder="输入你的 API Key (sk-...)"
                    value={apiKey}
                  />
                  {!showApiKey && apiKey && (
                    <div className="pointer-events-none absolute inset-0 flex items-center px-3 font-mono text-sm">
                      {"•".repeat(Math.min(apiKey.length, 40))}
                    </div>
                  )}
                </div>
                <Button onClick={() => setShowApiKey(!showApiKey)} size="icon" variant="outline">
                  {showApiKey ? <EyeOff size={16} /> : <Eye size={16} />}
                </Button>
              </div>
              {aiSettings?.configured ? (
                <p className="text-muted-foreground text-xs">
                  已保存凭证 {aiSettings.maskedSuffix ?? ""}
                  。不会再次显示原始值。
                </p>
              ) : (
                <p className="text-muted-foreground text-xs">尚未保存 API Key。</p>
              )}
            </div>

            <div className="flex flex-wrap gap-2 pt-2">
              <Button disabled={isSavingConnection} onClick={handleSaveAiSettings}>
                {isSavingConnection ? <Loader2 className="animate-spin" /> : <Save size={16} />}
                {isSavingConnection ? "验证中..." : "验证并保存连接"}
              </Button>
              {aiSettings?.configured && (
                <Button
                  disabled={isClearingCredential}
                  onClick={handleClearAiCredential}
                  variant="destructive"
                >
                  {isClearingCredential ? (
                    <Loader2 className="animate-spin" />
                  ) : (
                    <Trash2 size={16} />
                  )}
                  清除已保存凭证
                </Button>
              )}
            </div>

            <div className="space-y-4 border-t pt-4">
              <div className="space-y-2">
                <Label>默认模型</Label>
                <ModelSelect
                  allowCustom={false}
                  apiModels={apiModels}
                  commonModels={AVAILABLE_MODELS}
                  customValue=""
                  disabled={!canSelectModel}
                  isCustom={false}
                  onChange={setModel}
                  placeholder={isConnectionSaved ? "搜索或选择模型..." : "先保存 API 连接"}
                  value={model}
                />
              </div>

              <div className="flex items-center gap-2 text-muted-foreground text-xs">
                {isLoadingModels ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>正在加载模型列表...</span>
                  </>
                ) : isConnectionSaved && apiModels.length > 0 ? (
                  <span>已从当前服务商加载 {apiModels.length} 个模型</span>
                ) : isConnectionSaved ? (
                  <span>当前服务商没有返回可用模型，请重新验证连接</span>
                ) : (
                  <span>保存并验证连接后，才能选择模型</span>
                )}
              </div>
              <Button
                disabled={!canSelectModel || isSavingModel}
                onClick={handleSaveModelSettings}
                variant="outline"
              >
                {isSavingModel ? <Loader2 className="animate-spin" /> : <Save size={16} />}
                {isSavingModel ? "测试中..." : "测试并保存默认模型"}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* 项目管理 */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>我的项目</CardTitle>
                <CardDescription>管理 ACF 规划项目</CardDescription>
              </div>
              <Button onClick={() => setShowCreate(true)} size="sm" variant="outline">
                <Plus className="mr-1" size={16} />
                新建
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {!contexts || contexts.length === 0 ? (
              <p className="py-4 text-center text-muted-foreground text-sm">
                暂无项目，点击上方"新建"创建第一个
              </p>
            ) : (
              <div className="space-y-2">
                {contexts.map((ctx) => (
                  <div
                    className="flex items-center gap-3 rounded-lg border border-border px-4 py-3"
                    key={ctx.id}
                  >
                    {editingId === ctx.id ? (
                      <Input
                        autoFocus
                        className="flex-1"
                        onChange={(e) => setEditingName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            void handleRename();
                          }
                          if (e.key === "Escape") {
                            setEditingId(null);
                          }
                        }}
                        value={editingName}
                      />
                    ) : (
                      <Link
                        className="min-w-0 flex-1"
                        search={{ contextId: ctx.id }}
                        to="/site-tools/acf"
                      >
                        <span className="truncate font-medium text-foreground">{ctx.name}</span>
                        <span className="ml-2 text-muted-foreground text-xs">
                          {ctx.isCompleted ? "已完成" : "进行中"}
                        </span>
                      </Link>
                    )}

                    <div className="flex items-center gap-1">
                      {editingId === ctx.id ? (
                        <>
                          <Button
                            disabled={!editingName.trim() || updateContext.isPending}
                            onClick={handleRename}
                            size="icon"
                            variant="ghost"
                          >
                            <Check size={16} />
                          </Button>
                          <Button onClick={() => setEditingId(null)} size="icon" variant="ghost">
                            <X size={16} />
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button
                            onClick={() => {
                              setEditingId(ctx.id);
                              setEditingName(ctx.name);
                            }}
                            size="icon"
                            variant="ghost"
                          >
                            <Pencil className="text-muted-foreground" size={14} />
                          </Button>
                          <Button onClick={() => setDeletingId(ctx.id)} size="icon" variant="ghost">
                            <Trash2 className="text-muted-foreground" size={14} />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* 新建项目对话框 */}
      <Dialog onOpenChange={setShowCreate} open={showCreate}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>新建项目</DialogTitle>
          </DialogHeader>
          <Input
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                void handleCreate();
              }
            }}
            placeholder="项目名称（如：硅胶产品站）"
            value={newName}
          />
          <DialogFooter>
            <Button onClick={() => setShowCreate(false)} variant="outline">
              取消
            </Button>
            <Button disabled={!newName.trim() || createContext.isPending} onClick={handleCreate}>
              {createContext.isPending ? "创建中..." : "创建"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 删除确认对话框 */}
      <Dialog
        onOpenChange={(open) => {
          if (!open) {
            setDeletingId(null);
          }
        }}
        open={!!deletingId}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>确认删除</DialogTitle>
          </DialogHeader>
          <p className="text-muted-foreground text-sm">
            确定要删除项目「{deletingContext?.name}
            」吗？所有问答记录和生成结果将不可恢复。
          </p>
          <DialogFooter>
            <Button onClick={() => setDeletingId(null)} variant="outline">
              取消
            </Button>
            <Button disabled={deleteContext.isPending} onClick={handleDelete} variant="destructive">
              {deleteContext.isPending ? "删除中..." : "删除"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
